import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Eye, EyeOff, GraduationCap } from 'lucide-react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === 'string') setError(d);
      else if (Array.isArray(d)) setError(d.map(x => x.msg).filter(Boolean).join(' '));
      else setError(t('login.failed'));
    }
    setLoading(false);
  }

  return (
    <div data-testid="login-page" style={{ display: 'flex', height: '100vh', background: '#F8F9FB' }}>
      {/* Left - Form */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '2rem', minWidth: 0,
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2.5rem' }}>
            <div style={{
              width: 48, height: 48, borderRadius: '0.75rem', background: '#1E2140',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GraduationCap size={28} color="white" />
            </div>
            <div>
              <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
                FrenchBoard
              </h1>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0 }}>
                {t('login.teacherPortal')}
              </p>
            </div>
          </div>

          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '2rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
            {t('login.welcomeBack')}
          </h2>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1rem', color: '#64748B', marginBottom: '2rem' }}>
            {t('login.subtitle')}
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
                {t('login.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                data-testid="login-email-input"
                style={{
                  width: '100%', padding: '0.875rem 1rem', borderRadius: '0.75rem',
                  border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem', outline: 'none', background: 'white',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
            </div>

            <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
              <label style={{ display: 'block', fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
                {t('login.password')}
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
                data-testid="login-password-input"
                style={{
                  width: '100%', padding: '0.875rem 3rem 0.875rem 1rem', borderRadius: '0.75rem',
                  border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem', outline: 'none', background: 'white',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                data-testid="toggle-password-visibility"
                style={{
                  position: 'absolute', right: 12, bottom: 14,
                  background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8',
                  padding: '2px',
                }}
              >
                {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {error && (
              <div data-testid="login-error-message" style={{
                padding: '0.75rem 1rem', borderRadius: '0.75rem', background: '#FEF2F2',
                color: '#DC2626', fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem',
                marginBottom: '1.25rem', border: '1px solid #FECACA',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              style={{
                width: '100%', height: '3.25rem', borderRadius: '0.75rem',
                background: loading ? '#94A3B8' : '#1E2140', color: 'white',
                fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 600,
                border: 'none', cursor: loading ? 'default' : 'pointer',
                transition: 'background 0.2s, transform 0.1s',
              }}
              onMouseDown={e => !loading && (e.currentTarget.style.transform = 'scale(0.98)')}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>
        </div>
      </div>

      {/* Right - Hero */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1E2140 0%, #2C3075 50%, #1E2140 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ textAlign: 'center', padding: '3rem', zIndex: 1, maxWidth: 480 }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <img src="https://flagcdn.com/80x60/ca.png" alt="Canada" style={{ width: 60, height: 45, objectFit: 'contain' }} />
          </div>
          <h2 style={{
            fontFamily: 'Outfit, sans-serif', fontSize: '2.25rem', fontWeight: 700,
            color: 'white', marginBottom: '1rem', lineHeight: 1.2,
          }}>
            {t('login.heroTitle').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}
          </h2>
          <p style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem',
            color: 'rgba(255,255,255,0.65)', lineHeight: 1.7,
          }}>
            {t('login.heroDesc')}
          </p>

          <div style={{
            display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem',
            flexWrap: 'wrap',
          }}>
            {['PRE-A1', 'A1', 'A1-A2', 'A2'].map((lvl, i) => (
              <span key={lvl} style={{
                padding: '0.4rem 1rem', borderRadius: '2rem',
                background: ['#4ADE80', '#60A5FA', '#FACC15', '#FB923C'][i],
                fontFamily: 'Fredoka, sans-serif', fontWeight: 600, fontSize: '0.8rem',
                color: i === 2 ? '#1A1D20' : 'white',
              }}>
                {lvl}
              </span>
            ))}
          </div>
        </div>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'absolute', bottom: -80, left: -80, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
      </div>
    </div>
  );
}
